import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { employerAPI } from '../../services/api';
import { useCompare } from '../../contexts/CompareContext';
import { toast } from 'sonner';
import { 
  Loader2, Plus, User, MapPin, Briefcase, MoreVertical, Trash2, 
  Edit2, Eye, GripVertical, Users, Shield, X, Search, GitCompare
} from 'lucide-react';

const Pipeline = () => {
  const navigate = useNavigate();
  const { toggleCandidate, isSelected, canAddMore, selectedCandidates } = useCompare();
  const [loading, setLoading] = useState(true);
  const [stages, setStages] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [stagesRes, candidatesRes] = await Promise.all([
        employerAPI.getPipelineStages(),
        employerAPI.getPipelineCandidates()
      ]);
      setStages(stagesRes.data.stages);
      setCandidates(candidatesRes.data.candidates);
    } catch (error) {
      toast.error('Failed to load pipeline');
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = useCallback(async (result) => {
    const { destination, source, draggableId } = result;

    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    // Update local state optimistically
    const updatedCandidates = candidates.map(c => {
      if (c.id === draggableId) {
        return { ...c, stage_id: destination.droppableId };
      }
      return c;
    });
    setCandidates(updatedCandidates);

    // Update backend
    try {
      await employerAPI.updatePipelineCandidate(draggableId, { stage_id: destination.droppableId });
    } catch (error) {
      // Revert on failure
      setCandidates(candidates);
      toast.error('Failed to move candidate');
    }
  }, [candidates]);

  const handleRemoveCandidate = async (candidateId) => {
    try {
      await employerAPI.removeFromPipeline(candidateId);
      setCandidates(prev => prev.filter(c => c.id !== candidateId));
      toast.success('Candidate removed from pipeline');
    } catch (error) {
      toast.error('Failed to remove candidate');
    }
  };

  const handleSaveNotes = async () => {
    if (!selectedCandidate) return;
    setSavingNotes(true);
    try {
      await employerAPI.updatePipelineCandidate(selectedCandidate.id, { notes });
      setCandidates(prev => prev.map(c => 
        c.id === selectedCandidate.id ? { ...c, notes } : c
      ));
      setShowNotesModal(false);
      toast.success('Notes saved');
    } catch (error) {
      toast.error('Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  const openNotesModal = (candidate) => {
    setSelectedCandidate(candidate);
    setNotes(candidate.notes || '');
    setShowNotesModal(true);
  };

  const getCandidatesForStage = (stageId) => {
    return candidates.filter(c => c.stage_id === stageId);
  };

  const handleToggleCompare = (candidate) => {
    if (!isSelected(candidate.employee_id) && !canAddMore) {
      toast.error('Maximum 3 candidates can be compared');
      return;
    }
    toggleCandidate({
      employee_id: candidate.employee_id,
      name: candidate.name || `${candidate.first_name || ''} ${candidate.last_name || ''}`.trim(),
      photo_url: candidate.photo_url,
      job_title: candidate.job_title
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#1B5AFF]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="pipeline-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#0A0F1E]">Candidate Pipeline</h1>
            <p className="mt-1 text-[#5B6472] text-sm">
              Drag and drop candidates between stages to track your hiring process.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#F5F8FF] border border-[#E8EDF5]">
              <Users className="w-4 h-4 text-[#1B5AFF]" />
              <span className="text-[#0A0F1E] font-medium">{candidates.length}</span>
              <span className="text-[#5B6472] text-sm">Candidates</span>
            </div>
            <Button
              onClick={() => navigate('/employer/search')}
              className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl"
              data-testid="add-candidates-btn"
            >
              <Search className="w-4 h-4 mr-2" />
              Find Talent
            </Button>
          </div>
        </div>

        {/* Pipeline Board */}
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="flex gap-4 overflow-x-auto pb-4 -mx-4 px-4">
            {stages.map((stage) => (
              <div 
                key={stage.id} 
                className="flex-shrink-0 w-72"
                data-testid={`stage-${stage.id}`}
              >
                {/* Stage Header */}
                <div 
                  className="flex items-center justify-between p-3 rounded-t-xl border border-b-0 border-[#E8EDF5]"
                  style={{ backgroundColor: `${stage.color}15` }}
                >
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-3 h-3 rounded-full" 
                      style={{ backgroundColor: stage.color }}
                    />
                    <span className="font-semibold text-[#0A0F1E]">{stage.name}</span>
                  </div>
                  <span 
                    className="px-2 py-0.5 rounded-full text-xs font-medium"
                    style={{ backgroundColor: `${stage.color}30`, color: stage.color }}
                  >
                    {getCandidatesForStage(stage.id).length}
                  </span>
                </div>

                {/* Droppable Area */}
                <Droppable droppableId={stage.id}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`min-h-[400px] p-2 rounded-b-xl border border-t-0 border-[#E8EDF5] transition-colors ${
                        snapshot.isDraggingOver ? 'bg-[#FFFFFF]' : 'bg-[#0F0F1A]'
                      }`}
                    >
                      {getCandidatesForStage(stage.id).map((candidate, index) => (
                        <Draggable 
                          key={candidate.id} 
                          draggableId={candidate.id} 
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className={`mb-2 last:mb-0 ${snapshot.isDragging ? 'rotate-2' : ''}`}
                            >
                              <Card 
                                className={`bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-3 transition-all hover:border-[#1B5AFF]/50 ${
                                  snapshot.isDragging ? 'shadow-xl shadow-[#1B5AFF]/20' : ''
                                }`}
                                data-testid={`candidate-card-${candidate.id}`}
                              >
                                {/* Drag Handle + Content */}
                                <div className="flex items-start gap-2">
                                  <div 
                                    {...provided.dragHandleProps}
                                    className="mt-1 cursor-grab active:cursor-grabbing"
                                  >
                                    <GripVertical className="w-4 h-4 text-[#5B6472]" />
                                  </div>
                                  
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-start gap-3">
                                      {/* Avatar */}
                                      {candidate.photo_url ? (
                                        <img 
                                          src={candidate.photo_url} 
                                          alt={candidate.name}
                                          className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                                        />
                                      ) : (
                                        <div className="w-10 h-10 rounded-lg bg-[#F5F8FF] flex items-center justify-center flex-shrink-0">
                                          <User className="w-5 h-5 text-[#5B6472]" />
                                        </div>
                                      )}
                                      
                                      <div className="flex-1 min-w-0">
                                        <h4 className="text-[#0A0F1E] font-medium text-sm truncate">
                                          {candidate.name || 'Unknown'}
                                        </h4>
                                        {candidate.job_title && (
                                          <p className="text-[#5B6472] text-xs truncate flex items-center gap-1">
                                            <Briefcase className="w-3 h-3" />
                                            {candidate.job_title}
                                          </p>
                                        )}
                                        {candidate.vett_id && (
                                          <div className="flex items-center gap-1 mt-1">
                                            <Shield className="w-3 h-3 text-[#1B5AFF]" />
                                            <span className="text-[#1B5AFF] text-xs font-mono">{candidate.vett_id}</span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    
                                    {/* Notes Preview */}
                                    {candidate.notes && (
                                      <p className="text-[#5B6472] text-xs mt-2 line-clamp-2 italic">
                                        "{candidate.notes}"
                                      </p>
                                    )}
                                    
                                    {/* Actions */}
                                    <div className="flex items-center gap-1 mt-2 pt-2 border-t border-[#E8EDF5]">
                                      <button
                                        onClick={() => handleToggleCompare(candidate)}
                                        className={`h-7 px-2 rounded text-xs font-medium transition-colors ${
                                          isSelected(candidate.employee_id)
                                            ? 'bg-[#5B8AF0]/20 text-[#5B8AF0] border border-[#5B8AF0]/30'
                                            : 'text-[#5B6472] hover:text-[#0A0F1E] hover:bg-[#F5F8FF]'
                                        }`}
                                        data-testid={`compare-${candidate.id}`}
                                      >
                                        <GitCompare className="w-3 h-3 inline mr-1" />
                                        {isSelected(candidate.employee_id) ? 'Selected' : 'Compare'}
                                      </button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => navigate(`/employer/talent/${candidate.employee_id}`)}
                                        className="h-7 px-2 text-[#5B6472] hover:text-[#0A0F1E] hover:bg-[#F5F8FF]"
                                        data-testid={`view-profile-${candidate.id}`}
                                      >
                                        <Eye className="w-3 h-3 mr-1" />
                                        View
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => openNotesModal(candidate)}
                                        className="h-7 px-2 text-[#5B6472] hover:text-[#0A0F1E] hover:bg-[#F5F8FF]"
                                        data-testid={`edit-notes-${candidate.id}`}
                                      >
                                        <Edit2 className="w-3 h-3 mr-1" />
                                        Notes
                                      </Button>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleRemoveCandidate(candidate.id)}
                                        className="h-7 px-2 text-[#5B6472] hover:text-red-400 hover:bg-red-500/10"
                                        data-testid={`remove-${candidate.id}`}
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              </Card>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                      
                      {/* Empty State */}
                      {getCandidatesForStage(stage.id).length === 0 && (
                        <div className="flex flex-col items-center justify-center py-8 text-center">
                          <div className="w-10 h-10 rounded-full bg-[#F5F8FF] flex items-center justify-center mb-2">
                            <Users className="w-5 h-5 text-[#5B6472]" />
                          </div>
                          <p className="text-[#5B6472] text-xs">No candidates</p>
                          <p className="text-[#5B6472] text-xs">Drag here to add</p>
                        </div>
                      )}
                    </div>
                  )}
                </Droppable>
              </div>
            ))}
          </div>
        </DragDropContext>

        {/* Empty State if no candidates at all */}
        {candidates.length === 0 && (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-12 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#F5F8FF] flex items-center justify-center mb-4">
              <Users className="w-8 h-8 text-[#5B6472]" />
            </div>
            <h3 className="text-[#0A0F1E] font-semibold text-lg mb-2">Start Building Your Pipeline</h3>
            <p className="text-[#5B6472] mb-6 max-w-md mx-auto">
              Search for talent and add candidates to your pipeline to track them through your hiring process.
            </p>
            <Button
              onClick={() => navigate('/employer/search')}
              className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl"
            >
              <Search className="w-4 h-4 mr-2" />
              Find Talent
            </Button>
          </Card>
        )}
      </div>

      {/* Notes Modal */}
      <Dialog open={showNotesModal} onOpenChange={setShowNotesModal}>
        <DialogContent className="bg-[#FFFFFF] border-[#E8EDF5] text-[#0A0F1E] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-[#1B5AFF]" />
              Notes for {selectedCandidate?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add notes about this candidate..."
              className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl min-h-[150px]"
              data-testid="notes-textarea"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowNotesModal(false)}
              className="border-[#E8EDF5] text-[#0A0F1E] hover:bg-[#F5F8FF] rounded-xl"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveNotes}
              disabled={savingNotes}
              className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl"
              data-testid="save-notes-btn"
            >
              {savingNotes ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Notes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default Pipeline;
